import React from "react"
import { MDXRenderer } from "gatsby-plugin-mdx"
import { MDXProvider } from "@mdx-js/react"
import Markdown from 'react-markdown'
import { Link, graphql } from "gatsby"
import Layout from "../components/layout"
import Bio from "../components/Blog/Bio"
import SEO from "../components/seo"
import config from "../../config/website"
import Share from "../components/share"
import {TwitterShareButton} from 'react-share'
// import Link from "../components/link"
import RelatedPosts from "../components/Blog/RelatedPosts"
import {ArrowLeft} from "../components/icons/arrow-left-icon"

const CoffeeMug = () => (
  <svg
    className="w-5 h-5 lg:w-7 lg:h-7 flex-no-shrink fill-current"
    xmlns="http://www.w3.org/2000/svg"
    viewBox="-2 -2 600 600"
  >
    <path d="M422 211v-30H30v75c0 69.501 36.669 131.461 91.558 166h207.884c13.48-8.483 26.829-18.594 37.837-30H407c57.891 0 105-47.109 105-105v-76h-90zm60 76c0 41.353-33.647 75-75 75h-16.326C410.287 331.652 422 294.763 422 256v-15h60v46zM0 452c0 33.137 26.863 60 60 60h332c33.137 0 60-26.863 60-60H0z" />
    <g id="steam">
      <path d="M160.068 0l-16.641 24.961c5.962 3.97 9.375 10.356 9.375 17.52s-3.413 13.55-9.375 17.52c-14.238 9.492-22.734 25.371-22.734 42.48 0 17.109 8.496 32.988 22.734 42.48L160.067 120c-5.962-3.97-9.375-10.356-9.375-17.52s3.413-13.55 9.375-17.52c14.239-9.491 22.735-25.37 22.735-42.48 0-17.109-8.496-32.988-22.734-42.48zM235.068 0l-16.641 24.961c5.962 3.97 9.375 10.356 9.375 17.52s-3.413 13.55-9.375 17.52c-14.238 9.492-22.734 25.371-22.734 42.48 0 17.109 8.496 32.988 22.734 42.48L235.067 120c-5.962-3.97-9.375-10.356-9.375-17.52s3.413-13.55 9.375-17.52c14.239-9.491 22.735-25.37 22.735-42.48 0-17.109-8.496-32.988-22.734-42.48zM310.068 0l-16.641 24.961c5.962 3.97 9.375 10.356 9.375 17.52s-3.413 13.55-9.375 17.52c-14.238 9.492-22.734 25.371-22.734 42.48 0 17.109 8.496 32.988 22.734 42.48L310.067 120c-5.962-3.97-9.375-10.356-9.375-17.52s3.413-13.55 9.375-17.52c14.239-9.491 22.735-25.37 22.735-42.48 0-17.109-8.496-32.988-22.734-42.48z" />
    </g>
  </svg>
)

const ExternalLink = props => {
  if (
    props.href.includes("https://dannielhansel.com/") ||
    props.href[0] === "/"
  ) {
    return (
      <a
        {...props}
        href={props.href}
        className="font-semibold text-accent hover:underline hover:underline-offset-4 cursor-pointer"
      >
        {props.children}
      </a>
    )
  }
  return (
    <a
      {...props}
      href={props.href}
      className="font-semibold text-accent hover:underline hover:underline-offset-4 cursor-pointer"
      target="_blank"
      rel="noopener noreferrer"
    >
      {props.children}
    </a>
  )
}

const CODE = props => (
  <div
    {...props}
    // className="flex flex-wrap sm:text-xs lg:text-base mb-4 "
    className="text-left my-4 p-2 leading-tight"
  />
)

const components = {
  p: props => (
    <p
      {...props}
      className="font-sans text-md md:text-xl font-normal md:font-medium px-4 mt-10 leading-relaxed md:leading-loose"
    />
  ),
  h1: props => (
    <h1
      {...props}
      className="font-body2 text-accent text-2xl md:text-3xl lg:text-4xl font-semibold tracking-tighter mt-10 md:mt-20 lg:mt-28 px-4"
    />
  ),
  h2: props => (
    <h2
      {...props}
      className="font-body2 text-accent text-xl md:text-2xl lg:text-3xl font-semibold tracking-tighter mt-8 md:mt-16 lg:mt-24 px-4"
    />
  ),
  h3: props => (
    <h3
      {...props}
      className="font-body2 text-accent text-lg md:text-xl lg:text-2xl font-semibold tracking-tighter mt-6 md:mt-12 lg:mt-20 px-4"
    />
  ),
  strong: props => (
    <strong
      {...props}
      className="font-bold"
      style={{ display: "inline" }}
    />
  ),
  a: ExternalLink,
  ul: props => <ul {...props} className="list-disc text-md md:text-xl font-normal md:font-medium mt-4 md:mt-6 lg:mt-8 px-8 md:px-9" />,
  li: props => (
    <li
      {...props}
      className="text-md md:text-xl font-normal md:font-medium my-2 md:my-4 px-4"
    />
  ),
  ol: props => (
    <ol
      {...props}
      className="list-decimal text-md md:text-xl font-normal md:font-medium mt-4 md:mt-6 lg:mt-8 px-8 md:px-9"
    />
  ),
  blockquote: props => (
    <blockquote
      {...props}
      // role="alert"
      className="bg-sky-600/20 text-sky-500 dark:bg-emerald-600/20 bg-blend-darker dark:text-emerald-500 border-l-8 border-sky-600 dark:border-emerald-600 text-md md:text-2xl font-bold md:font-extrabold"
    />
  ),
  // code: CODE,
  code: props => (
    <code
      {...props}
      className="flex flex-wrap font-mono sm:text-xs lg:text-base my-4 w-full overflow-x-auto"
      // className="text-left my-4 p-2 leading-tight"
      // style={{ color: "rebeccapurple" }}
    />
  ),
  inlinecode: props => <div {...props} className="" />,
}

const blogPost = ({ data, location }) => {
  const { frontmatter, body, timeToRead, excerpt, fields } = data.mdx
  const siteTitle = data.site.siteMetadata?.title || frontmatter.title
  const blogPostUrl = `${config.siteUrl}${fields.slug}`
  return (
    <>
      <MDXProvider components={components}>
        <Layout title={siteTitle} location={location}>
          <SEO 
            title={frontmatter.title} 
            description={frontmatter.description}
            keywords={frontmatter.tags} 
            // postData={frontmatter} 
            frontmatter={frontmatter} 
            metaImage={frontmatter.bannerUrl}
            pathname={fields.slug}
            isBlogPost
          />
          <section
            className=""
            style={{ minHeight: "80vh" }}
          >
            <article
              itemScope
              itemType="http://schema.org/Article"
            >
              <div className="w-10/12 lg:w-1/2 xl:w-7/12 my-8 mx-auto">
                <Link to="/blog/#searchBlog" className="flex justify-start items-start">
                  <button className="mt-10 mb-16 inline-block w-auto font-bold text-sm sm:text-md md:text-lg lg:text-2xl cursor-pointer transition duration-300 ease-in-out transform hover:-translate-y-1 hover:scale-100 border-2 border-blue-200 dark:border-gray-600  hover:border-blue-500 dark:hover:border-white rounded-full">
                    <div class="flex flex-cols-2 px-6 py-4 space-x-3 md:px-11 md:py-6 md:space-x-5">
                      <ArrowLeft className="w-4 h-4 lg:w-8 lg:h-8" />
                      <span className="relative flex items-center justify-center w-full h-full whitespace-nowrap text-secondary-text hover:text-quarternary ">See more blogs</span>
                    </div> 
                  </button>
                </Link>
                <header className="px-4 leading-relaxed mt-4">
                  <h1 className="font-body2 text-2xl md:text-4xl lg:text-5xl font-bold text-quarternary" itemProp="headline">
                    {frontmatter.title}
                  </h1>
                  <small className="overflow-hidden">
                    <p className="mt-4 text-md md:text-xl text-secondary-text flex">
                      {frontmatter.date}
                      {/* <span className="lg:hidden"> */}
                      <span className="text-md md:text-xl">
                        &nbsp;&bull;&nbsp;{timeToRead} min read &bull;&nbsp;
                      </span>
                      <div className="inline-block">
                        <CoffeeMug />
                      </div>
                    </p>
                  </small>
                </header>
                {frontmatter.bannerUrl ? (
                  <div className="overflow-x-hidden">
                    <div className="container mx-auto">
                      <div className="text-center my-8">
                        <img 
                          src={frontmatter.bannerUrl}
                          alt={frontmatter.tags}
                          className="rounded-md shadow-xl mb-4 object-scale-down h-full w-full"
                        />
                        {frontmatter.bannerCredit ? <Markdown>{frontmatter.bannerCredit}</Markdown> : null}
                      </div>
                    </div>
                  </div>
                  
                ) : null}
                <div className="">
                  <MDXRenderer>{body}</MDXRenderer>
                </div>
                <div className="mt-36 flex justify-between text-accent text-xs md:text-xl font-normal md:font-semibold leading-relaxed md:leading-loose">
                  <p className="text-start flex flex-col md:flex-row">
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Discus on Twitter"
                      href={`https://twitter.com/search?q=${encodeURIComponent(
                        blogPostUrl,
                      )}`}
                    >
                      Discuss on Twitter
                    </a>
                    <span className="hidden md:block mx-2">{` • `}</span>
                    <a
                        target="_blank"
                        rel="noopener noreferrer"
                        href="https://github.com/dehanz13"
                        ria-label="Edit on GitHub"
                      >
                        Edit on GitHub
                    </a>
                  </p>
                  <TwitterShareButton
                    url={blogPostUrl}
                    title={frontmatter.title}
                    via={config.twitterHandle.split('@').join('')}
                    className="text-end cursor-pointer"
                  >
                    Tweet this article
                  </TwitterShareButton>
                </div>
                <div className="mb-10">
                  <Share 
                    url={blogPostUrl}
                    title={frontmatter.title}
                    twitterHandle={config.twitterHandle}
                  />
                </div>
                <footer>
                  <Bio />
                </footer>
              </div>
            </article>
            <nav>
              <RelatedPosts content={data.relatedMdxs.posts} />
            </nav>
          </section>
        </Layout>
      </MDXProvider>
    </>
  )
}
export const pageQuery = graphql`
  query BlogPostQuery($id: String) {
    site {
      siteMetadata {
        title
      }
    }
    mdx(id: { eq: $id }) {
      id
      excerpt(pruneLength: 160)
      body
      timeToRead
      fields {
        slug
      }
      frontmatter {
        slug
        title
        author
        bannerUrl
        bannerCredit
        slug
        tags
        description
        date(formatString: "MMMM DD, YYYY")
      }
    }
    relatedMdxs(parent: { id: { eq: $id } }) {
      posts {
        id
        timeToRead
        slug
        frontmatter {
          title
          author
          bannerUrl
          bannerCredit
          slug
          tags
          description
          date(formatString: "MMMM DD, YYYY")
        }
      }
    }
  }
`
export default blogPost
